/**
 * Full ERP navigation tree.
 * Mỗi module có path duy nhất dùng làm route (hoặc query param cho tab con).
 * Sidebar render đệ quy từ cây này.
 */
import {
  Home,
  Dashboard,
  ShoppingCart,
  Store,
  ShowChart,
  Factory,
  CompareArrows,
  Science,
  Warehouse,
  Storage,
  Settings,
  Person,
  AccountBalance,
  Analytics,
  Assignment,
  Assessment,
  Badge,
  Business,
  Category,
  ContentPasteSearch,
  CurrencyExchange,
  Description,
  FactCheck,
  Groups,
  Inventory,
  LocalShipping,
  ManageAccounts,
  Paid,
  Payments,
  ReceiptLong,
  Rule,
  Scale,
  Security,
  Summarize,
  SwapHoriz,
  TrendingUp,
} from '@mui/icons-material'
import type { SvgIconComponent } from '@mui/icons-material'

export interface NavItem {
  key: string
  label: string
  path: string
  icon?: SvgIconComponent
  /** Permission key từ settings/roles. Item bị ẩn nếu role có key này = NoGrant (2). */
  permKey?: string
  children?: NavItem[]
}

export const navigation: NavItem[] = [
  { key: 'home', label: 'Home', path: '/home', icon: Home },

  // ─── Report ────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Report',
    path: '/dashboard',
    icon: Dashboard,
    children: [
      { key: 'report-pnl', label: 'PnL', path: '/dashboard/pnl', icon: Analytics },
      { key: 'report-risk', label: 'Risk Management', path: '/dashboard/risk-management', icon: Security },
      { key: 'report-long-short', label: 'Long Short', path: '/dashboard/long-short', icon: SwapHoriz },
      { key: 'report-cashflows', label: 'Cashflows', path: '/dashboard/cashflows', icon: Paid },
    ],
  },

  // ─── Sales ───────────────────────────────────────────────────────
  {
    key: 'sales',
    label: 'Sales',
    path: '/sales',
    icon: Store,
    children: [
      {
        key: 'sales-contract',
        label: 'Contract',
        path: '/sales/contract',
        icon: Description,
        permKey: 'saleContract',
        children: [
          { key: 'sale-contracts', label: 'Sale Contracts', path: '/sales/contract/list' },
          { key: 'consignment', label: 'Consignment', path: '/sales/contract/consignment' },
          { key: 'sales-amendment', label: 'Amendment', path: '/sales/contract/amendment' },
          { key: 'sales-ptbf-fixation', label: 'PTBF Fixation', path: '/sales/contract/ptbf-fixation' },
          { key: 'sales-contract-references', label: 'References', path: '/sales/contract/references' },
        ],
      },
      {
        key: 'sales-control',
        label: 'Control',
        path: '/sales/control',
        icon: FactCheck,
        permKey: 'saleControl',
        children: [
          { key: 'local-sales', label: 'Local Sales', path: '/sales/control/local-sales' },
          { key: 'sales-allocation', label: 'Sales Allocation', path: '/sales/control/sales-allocation' },
          { key: 'delivery-order', label: 'Delivery Order', path: '/sales/control/delivery-order' },
          { key: 'receive-order', label: 'Receive Order', path: '/sales/control/receive-order' },
          { key: 'sales-invoice', label: 'Invoice', path: '/sales/control/invoice' },
          { key: 'claim', label: 'Claim', path: '/sales/control/claim' },
          { key: 'credit-note', label: 'Credit Note', path: '/sales/control/credit-note' },
          { key: 'debit-note', label: 'Debit Note', path: '/sales/control/debit-note' },
          { key: 'moving', label: 'Moving', path: '/sales/control/moving' },
          { key: 'sales-references', label: 'References', path: '/sales/control/references' },
        ],
      },
    ],
  },

  // ─── Purchase ────────────────────────────────────────────────────
  {
    key: 'purchase',
    label: 'Purchase',
    path: '/purchase',
    icon: ShoppingCart,
    children: [
      {
        key: 'purchase-contract',
        label: 'Contract',
        path: '/purchase/contract',
        icon: Assignment,
        permKey: 'purchase',
        children: [
          { key: 'pc-localP', label: 'PU Contract', path: '/purchase/contract/local-p' },
          { key: 'pc-contractsAll', label: 'All Contracts', path: '/purchase/contract/all-contracts' },
          { key: 'pc-npeContract', label: 'NPE Contract', path: '/purchase/contract/npe-contract' },
          { key: 'pc-amendment', label: 'Amendment', path: '/purchase/contract/amendment' },
          { key: 'pc-ptbfFixation', label: 'PTBF Fixation', path: '/purchase/contract/ptbf-fixation' },
          { key: 'pc-ptbfRolling', label: 'PTBF Rolling', path: '/purchase/contract/ptbf-rolling' },
          { key: 'pc-npeFixation', label: 'NPE Fixation', path: '/purchase/contract/npe-fixation' },
          { key: 'pc-npeAllocation', label: 'NPE Allocation', path: '/purchase/contract/npe-allocation' },
          { key: 'pc-csFixation', label: 'CS Fixation', path: '/purchase/contract/cs-fixation' },
          { key: 'pc-forwardP', label: 'Forward PU', path: '/purchase/contract/forward-pu' },
          { key: 'pc-plans', label: 'Plans', path: '/purchase/contract/plans' },
          { key: 'pc-references', label: 'References', path: '/purchase/contract/references' },
        ],
      },
      {
        key: 'purchase-control',
        label: 'Control',
        path: '/procurement',
        icon: FactCheck,
        permKey: 'procurement',
        children: [
          { key: 'procurement-purchase', label: 'Purchase', path: '/procurement/purchase' },
          { key: 'procurement-contract-detail', label: 'Contract Detail', path: '/procurement/contract-detail' },
          { key: 'procurement-pay-data', label: 'Pay Data', path: '/procurement/pay-data' },
          { key: 'procurement-invoice', label: 'Invoice', path: '/procurement/invoice' },
          { key: 'procurement-good-allocation', label: 'Good Allocation', path: '/procurement/good-allocation' },
          { key: 'procurement-references', label: 'References', path: '/procurement/references' },
          { key: 'procurement-cvbb', label: 'Công văn & Biên bản', path: '/procurement/cvbb' },
        ],
      },
    ],
  },

  // ─── Futures Control ─────────────────────────────────────────────
  {
    key: 'futures',
    label: 'Futures Control',
    path: '/futures',
    icon: ShowChart,
    permKey: 'futures',
    children: [
      { key: 'futures-summary', label: 'Summary', path: '/futures/summary', icon: Summarize },
      { key: 'margin-report', label: 'Margin Report', path: '/futures/margin-report', icon: Assessment },
      { key: 'market', label: 'Market', path: '/futures/market', icon: ShowChart },
      { key: 'futures-allocation', label: 'Allocation', path: '/futures/allocation', icon: Rule },
      { key: 'futures-orders', label: 'Futures Orders', path: '/futures/orders', icon: ReceiptLong },
      { key: 'spreads-orders', label: 'Spreads Orders', path: '/futures/spreads', icon: SwapHoriz },
      { key: 'local-p', label: 'Local P', path: '/futures/local-p', icon: Paid },
      { key: 'local-s', label: 'Local S', path: '/futures/local-s', icon: Paid },
      { key: 'bank-cash-in', label: 'Bank Cash In', path: '/futures/bank-cash-in', icon: AccountBalance },
      { key: 'bank-statement', label: 'Bank Statement', path: '/futures/bank-statement', icon: Description },
      { key: 'adjust-lot', label: 'Adjust Lot', path: '/futures/adjust-lot', icon: Scale },
      { key: 'bank-cost-change', label: 'Bank Cost Change', path: '/futures/bank-cost-change', icon: CurrencyExchange },
      { key: 'cash-balance', label: 'Cash Balance', path: '/futures/cash-balance', icon: Payments },
      { key: 'futures-position', label: 'Position', path: '/futures/position', icon: TrendingUp, permKey: 'position' },
      { key: 'pnl', label: 'PnL', path: '/futures/pnl', icon: Analytics },
      { key: 'check-futures', label: 'Check Futures', path: '/futures/check-futures', icon: FactCheck },
      { key: 'reconciliation', label: 'Reconciliation', path: '/futures/reconciliation', icon: ContentPasteSearch },
    ],
  },

  // ─── Factory ─────────────────────────────────────────────────────
  {
    key: 'factory',
    label: 'Factory',
    path: '/factory',
    icon: Factory,
    children: [
      { key: 'bao-loc', label: 'Bao Loc', path: '/factory/bao-loc', icon: Factory, permKey: 'factoryBl' },
      { key: 'nguyen-cuong', label: 'Nguyen Cuong', path: '/factory/nguyen-cuong', icon: Factory, permKey: 'factoryNc' },
      { key: 'nong-san', label: 'Nong San', path: '/factory/nong-san', icon: Factory, permKey: 'factoryNs' },
      { key: 'nhon-trach', label: 'Nhon Trach', path: '/factory/nhon-trach', icon: Factory, permKey: 'factoryNt' },
      { key: 'pacorini', label: 'Pacorini', path: '/factory/pacorini', icon: Factory, permKey: 'factoryPaco' },
      { key: 'external-factory', label: 'External Factory', path: '/factory/external', icon: Business, permKey: 'factory' },
      { key: 'factory-summary', label: 'Factory Summary', path: '/factory/summary', icon: Summarize, permKey: 'factory' },
    ],
  },

  // ─── Matching P/S ────────────────────────────────────────────────
  {
    key: 'matching',
    label: 'Matching P/S',
    path: '/matching',
    icon: CompareArrows,
    permKey: 'matchingPs',
    children: [
      { key: 'data-plan', label: 'Data Plan', path: '/matching/data-plan', icon: Summarize },
      { key: 'direct-matching', label: 'Direct Matching', path: '/matching/direct', icon: CompareArrows },
      { key: 'purchase-stock', label: 'Purchase Stock', path: '/matching/purchase-stock', icon: ShoppingCart },
      { key: 'sales-stock', label: 'Sales Stock', path: '/matching/sales-stock', icon: Store },
      { key: 'local-purchase-matching', label: 'Local Purchase Matching', path: '/matching/local-purchase', icon: Inventory },
      { key: 'local-sales-matching', label: 'Local Sales Matching', path: '/matching/local-sales', icon: Inventory },
      { key: 'contract-overview', label: 'Contract Overview', path: '/matching/contract-overview', icon: Description },
      { key: 'stock-quality-report', label: 'Stock Quality Report', path: '/matching/stock-quality', icon: Science },
    ],
  },

  // ─── Quality ─────────────────────────────────────────────────────
  {
    key: 'quality',
    label: 'Quality',
    path: '/quality',
    icon: Science,
    permKey: 'quality',
    children: [
      { key: 'sampling', label: 'Sampling', path: '/quality/sampling', icon: Science },
      { key: 'sampling-data', label: 'Sampling Data', path: '/quality/sampling-data', icon: FactCheck },
      { key: 'pss-label', label: 'Pr PSS Label', path: '/quality/pss-label', icon: Badge },
      { key: 'nestle-sample', label: 'Nestle Sample', path: '/quality/nestle-sample', icon: LocalShipping },
    ],
  },

  // ─── Virtual Warehouse ───────────────────────────────────────────
  {
    key: 'virtual-warehouse',
    label: 'Virtual Warehouse',
    path: '/virtual-warehouse',
    icon: Warehouse,
    permKey: 'virtualWarehouse',
    children: [
      { key: 'vw-received', label: 'Received', path: '/virtual-warehouse/received', icon: Inventory },
      { key: 'quality-discount', label: 'Quality Discount', path: '/virtual-warehouse/quality-discount', icon: Science },
      { key: 'delivery-note-print', label: 'Delivery Note Print', path: '/virtual-warehouse/delivery-note-print', icon: LocalShipping },
      { key: 'receive-note-print', label: 'Receive Note Print', path: '/virtual-warehouse/receive-note-print', icon: ReceiptLong },
    ],
  },


  // ─── Master Data ─────────────────────────────────────────────────
  {
    key: 'master-data',
    label: 'Master Data',
    path: '/master-data',
    icon: Storage,
    permKey: 'masterData',
    children: [
      { key: 'md-futures', label: 'Futures', path: '/master-data/futures', icon: ShowChart },
      { key: 'md-items', label: 'Items', path: '/master-data/items', icon: Category },
      { key: 'md-counterparty', label: 'Counterparty', path: '/master-data/counterparty', icon: Groups },
      { key: 'md-bank', label: 'Bank', path: '/master-data/bank', icon: AccountBalance },
      { key: 'md-certification', label: 'Certification', path: '/master-data/certification', icon: Badge },
      { key: 'md-consignee', label: 'Consignee', path: '/master-data/consignee', icon: Business },
      { key: 'md-contract-type', label: 'Contract Type', path: '/master-data/contract-type', icon: Assignment },
      { key: 'md-delivery-place', label: 'Delivery Place', path: '/master-data/delivery-place', icon: Warehouse },
      { key: 'md-delivery-term', label: 'Delivery Term', path: '/master-data/delivery-term', icon: LocalShipping },
      { key: 'md-package', label: 'Package', path: '/master-data/package', icon: Inventory },
      { key: 'md-warehouse', label: 'Warehouse', path: '/master-data/warehouse', icon: Warehouse },
      { key: 'md-vendor', label: 'Vendor', path: '/master-data/vendor', icon: Business },
      { key: 'md-vessel-name', label: 'Vessel Name', path: '/master-data/vessel-name', icon: LocalShipping },
    ],
  },

  // ─── Settings ────────────────────────────────────────────────────
  {
    key: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: Settings,
    permKey: 'setting',
    children: [
      { key: 'roles', label: 'Roles', path: '/settings/roles', icon: ManageAccounts },
      { key: 'users', label: 'Users', path: '/settings/users', icon: Person },
      { key: 'log-history', label: 'Log History', path: '/settings/log-history', icon: ContentPasteSearch },
    ],
  },
]

/**
 * Flatten toàn bộ nav tree thành mảng leaf paths (cho route generation).
 */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  const result: NavItem[] = []
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      result.push(...flattenNavItems(item.children))
    } else {
      result.push(item)
    }
  }
  return result
}
